/**
 * Subcontractor Approvals — review pending labour subcontractors (incl. ones
 * added from the site kiosk) and Approve / Reject them. Approving auto-confirms
 * any held kiosk attendance. Approve/Reject is restricted to Procurement, IT,
 * HR, MD/CEO/CMD and super-admin (others can view the list only).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, ShieldCheck, Hourglass, Building2, User } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut } from '../lib/api';
import { useUrlTab } from '../lib/hooks/useUrlState';

const TABS = [
  { key: 'pending_approval', label: 'Pending', icon: Hourglass },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
  { key: 'rejected', label: 'Rejected', icon: XCircle },
  { key: 'all', label: 'All', icon: Building2 },
];

const STATUS_STYLE = {
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

import { formatDateTime as fmt } from '../lib/format';

export default function SubcontractorApprovals() {
  const [tab, setTab] = useUrlTab('tab', 'pending_approval');
  const [items, setItems] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/labour/subcontractors?status=${tab}`);
      setItems(r.items || []);
      setCanApprove(!!r.can_approve);
    } catch {
      toast.error('Could not load subcontractors');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (s) => {
    setBusyId(s.id);
    try {
      await apiPut(`/labour/subcontractors/${s.id}/approve`);
      toast.success(`Approved — ${s.name}. Any held kiosk attendance will be confirmed.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally { setBusyId(null); }
  };

  const reject = async (s) => {
    const reason = window.prompt(`Reject "${s.name}"? Enter a reason:`);
    if (reason == null) return;
    if (reason.trim().length < 2) { toast.error('A short reason is required'); return; }
    setBusyId(s.id);
    try {
      await apiPut(`/labour/subcontractors/${s.id}/reject`, { reason: reason.trim() });
      toast.success(`Rejected — ${s.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejection failed');
    } finally { setBusyId(null); }
  };

  const pendingCount = items.filter((s) => s.status === 'pending_approval').length;

  return (
    <div data-testid="subcontractor-approvals-page">
      <PageHeader
        overline="Labour · Vendors"
        title="Subcontractor Approvals"
        subtitle="Review and approve subcontractors submitted by site teams or the gate kiosk. Approving instantly confirms any worker attendance held against them."
      />

      {!canApprove && (
        <div className="mb-4 flex items-center gap-2 text-xs bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-slate-600" data-testid="sca-readonly-banner">
          <ShieldCheck size={14} /> You can view submissions, but only Procurement, IT, HR, MD / CEO / CMD or an admin can approve or reject.
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="sca-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`sca-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                active ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-[#3b4872]'
              }`}
            >
              <Icon size={13} /> {t.label}
              {t.key === 'pending_approval' && pendingCount > 0 && active && (
                <span className="ml-1 bg-white/25 rounded-full px-1.5">{pendingCount}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="sca-empty">
            {tab === 'pending_approval' ? 'No subcontractors awaiting approval. ✓' : 'Nothing here yet.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sca-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-slate-800">{s.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-display font-bold uppercase ${STATUS_STYLE[s.status] || 'bg-slate-100 text-slate-500'}`}>
                      {(s.status || '').replace('_', ' ')}
                    </span>
                    {s.submitted_via === 'kiosk' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-display font-bold uppercase">via kiosk</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><User size={11} /> {s.submitted_by || '—'}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {fmt(s.submitted_at || s.created_at)}</span>
                    {s.status === 'rejected' && s.rejection_reason && (
                      <span className="text-rose-600">Reason: {s.rejection_reason}</span>
                    )}
                    {s.status === 'approved' && s.approved_by && (
                      <span className="text-emerald-600">Approved by {s.approved_by}</span>
                    )}
                  </div>
                </div>

                {canApprove && s.status === 'pending_approval' && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => approve(s)}
                      disabled={busyId === s.id}
                      data-testid={`sca-approve-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle2 size={13} /> Approve
                    </button>
                    <button
                      onClick={() => reject(s)}
                      disabled={busyId === s.id}
                      data-testid={`sca-reject-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <XCircle size={13} /> Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
